import * as d3 from 'd3'
import type { NodeMark } from '../utilities/nodeMarks'
import { CLOUD_PLACE_ICON, CLOUD_PLACE_SIZE } from './constants'
import {
  AWS_PATH,
  AWS_VIEWBOX,
  CLOUD_PATH,
  CLOUD_VIEWBOX,
  GROK_BOT_URL,
  NOBGP_ORANGE,
  TAILSCALE_PATH,
  TAILSCALE_VIEWBOX,
} from '../utilities/nodeMarkIcons'

/**
 * The marks after a node's name on the graph and the map (nobgp-router#2010): Grok Bot, noBGP
 * cloud or AWS, then Tailscale when it runs. The rule is `utilities/nodeMarks.ts`, and the node
 * list draws the same icons (`components/NodeMarks.tsx`).
 *
 * ⚠ TWO HALVES, because an SVG `<text>` cannot hold an icon. `renderer.label` ends the name with a
 * SPACER (`marksSpacer`): TRANSPARENT letters as wide as the icons (`styles.ts`). Not blank
 * characters: an em space in an SVG name measured 4 px, the width of a plain space. So every
 * `getBBox` of the name — the map's badge (`badgeOf`), its measure and its ring — already holds
 * the icons, and nothing else has to know about them. Then `placeMarks` draws each icon on its slot of the spacer, in the
 * node's point group: the group has the name's transform, so the two share one space.
 */

/** Before the first icon: a gap from the name. */
const GAP = ' '
/** Each icon's slot: a W, about as wide as the name's font is tall. AWS's logo is wider: Wn. */
const SLOTS: Record<NodeMark, string> = { grok: 'W', nobgp: 'W', aws: 'Wn', tailscale: 'W' }
/** Width over height of each icon. */
const ASPECT: Record<NodeMark, number> = { grok: 1, nobgp: 1, aws: 1.25, tailscale: 1 }
/** How much of its slot an icon fills, so that two icons do not touch. */
const FILL = 0.85

export function marksOf(d: GraphNode): NodeMark[] {
  return d.marks ? (d.marks.split(',') as NodeMark[]) : []
}

/** The letters `renderer.label` puts after a name with marks, or '' for none. */
export function marksSpacer(marks: NodeMark[]): string {
  return marks.length ? GAP + marks.map(mark => SLOTS[mark]).join('') : ''
}

/**
 * Draw the marks of each node on its name's spacer. ⚠ Call it after every pass that draws or
 * moves a name (its `x`, `y`, anchor or text): the icons are placed from where the spacer IS.
 * A point group that is redrawn loses its marks, and this draws them again.
 *
 * The marks take the name's classes, so `styles.ts` hides them where it hides the name.
 */
export function placeMarks(
  points: d3.Selection<SVGGElement, GraphNode, d3.BaseType, unknown>,
  labels: d3.Selection<SVGTextElement, GraphNode, d3.BaseType, unknown>
) {
  const names = new Map<string, SVGTextElement>()
  labels.each((d, i, all) => names.set(d.id, all[i]))
  points.each((d, i, all) => {
    const group = d3.select(all[i])
    const marks = marksOf(d)
    const name = names.get(d.id)
    const spacer = name?.querySelector<SVGTSpanElement>(':scope > tspan.marks-space')
    if (!marks.length || !name || !spacer || spacer.getNumberOfChars() !== marksSpacer(marks).length) {
      group.selectAll(':scope > g.marks').remove()
      return
    }
    let char = GAP.length
    const boxes = marks.map(mark => {
      const first = spacer.getExtentOfChar(char)
      const last = spacer.getExtentOfChar(char + SLOTS[mark].length - 1)
      char += SLOTS[mark].length
      const width = last.x + last.width - first.x
      const height = Math.min(first.height, width / ASPECT[mark]) * FILL
      return {
        x: first.x + (width - height * ASPECT[mark]) / 2,
        y: first.y + (first.height - height) / 2,
        width: height * ASPECT[mark],
        height,
      }
    })
    // ⚠ Redrawn only when a mark or its place changes. The map calls this on each move of the
    // map, and a pan moves the group, not the name in it: the icons stay as they are.
    const key = marks
      .map((mark, k) => `${mark}@${boxes[k].x.toFixed(1)},${boxes[k].y.toFixed(1)},${boxes[k].height.toFixed(1)}`)
      .join(' ')
    let layer = group.select<SVGGElement>(':scope > g.marks')
    if (layer.empty() || layer.attr('data-key') !== key) {
      layer.remove()
      layer = group.append('g').attr('data-key', key)
      marks.forEach((mark, k) => drawMark(layer, mark, boxes[k]))
    }
    // The name's states, without `label`. ⚠ `hidden` is `dimmed` here, as on the map's chips
    // (`placeChips`): the global `.hidden` rule sets opacity 0, and a name that is `hidden` because
    // another node is lit stays visible, dimmed — so must its marks.
    layer.attr(
      'class',
      [
        'marks',
        ...(name.getAttribute('class') ?? '')
          .split(' ')
          .filter(c => c && c !== 'label')
          .map(c => (c === 'hidden' ? 'dimmed' : c)),
      ].join(' ')
    )
  })
}

function drawMark(
  layer: d3.Selection<SVGGElement, unknown, null, undefined>,
  mark: NodeMark,
  box: { x: number; y: number; width: number; height: number }
) {
  if (mark === 'grok') {
    layer
      .append('image')
      .attr('href', GROK_BOT_URL)
      .attr('x', box.x)
      .attr('y', box.y)
      .attr('width', box.width)
      .attr('height', box.height)
    return
  }
  const [viewBox, path] =
    mark === 'nobgp'
      ? [CLOUD_VIEWBOX, CLOUD_PATH]
      : mark === 'aws'
        ? [AWS_VIEWBOX, AWS_PATH]
        : [TAILSCALE_VIEWBOX, TAILSCALE_PATH]
  const icon = layer
    .append('svg')
    .attr('class', mark)
    .attr('viewBox', viewBox)
    .attr('x', box.x)
    .attr('y', box.y)
    .attr('width', box.width)
    .attr('height', box.height)
  // ⚠ The orange is an attribute, the ink is in `styles.ts` (it follows the theme). Neither is
  // inherited from the point group, whose `fill` and `stroke` change on hover and selection.
  const drawn = icon.append('path').attr('d', path)
  if (mark === 'nobgp') drawn.attr('fill', NOBGP_ORANGE)
}

/**
 * A cloud location's glyph (`CloudPlace`), on the graph and on the map: the cloud's icon, large
 * (Benoit, 2026-10-20: "we will be able to use large icons that are more readable"). The same icon
 * as the cloud's mark after a node's name, which a node in that location does not carry.
 *
 * ⚠ The box is `.box`, as the data center's is on the map, and `styles.ts` gives it the same
 * states: lit, selected, streaming. At rest it draws nothing (Benoit, 2026-10-21: no rounded
 * square, so the icon can be bigger), and it is the hit area.
 */
export function drawCloudPlace(group: d3.Selection<SVGGElement, unknown, null, undefined>, place: CloudPlace) {
  const glyph = group.append('g').attr('class', `cloud-place ${place}`)
  const half = CLOUD_PLACE_SIZE / 2
  glyph.append('rect').attr('class', 'box').attr('x', -half).attr('y', -half).attr('width', CLOUD_PLACE_SIZE).attr('height', CLOUD_PLACE_SIZE).attr('rx', 9)
  const icon = CLOUD_PLACE_ICON
  drawMark(glyph, place, { x: -icon / 2, y: -icon / 2, width: icon, height: icon })
  return glyph
}
